import { useState, useEffect, useCallback } from 'react';

interface MessageNotification {
    id: string;
    sender: 'Pixel' | 'Reka' | 'System';
    preview: string;
    timestamp: number;
    isRead: boolean;
}

export const useMessageNotifications = () => {
    const [notifications, setNotifications] = useState<MessageNotification[]>([]);
    const [isMessengerOpen, setIsMessengerOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(() => {
        // Charger le compteur de messages non lus
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                return data.unreadMessageCount || 0;
            } catch (e) {}
        }
        return 0;
    });

    // Son de notification synthétisé
    const playNotificationSound = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            
            // Son de notification style années 90
            const osc1 = ctx.createOscillator();
            const osc2 = ctx.createOscillator();
            const gain = ctx.createGain();

            osc1.type = 'square';
            osc1.frequency.setValueAtTime(800, ctx.currentTime);
            osc1.frequency.setValueAtTime(600, ctx.currentTime + 0.1);

            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(400, ctx.currentTime);
            osc2.frequency.setValueAtTime(300, ctx.currentTime + 0.1);

            gain.gain.setValueAtTime(0.1, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);

            osc1.connect(gain);
            osc2.connect(gain);
            gain.connect(ctx.destination);

            osc1.start();
            osc2.start();
            osc1.stop(ctx.currentTime + 0.2);
            osc2.stop(ctx.currentTime + 0.2);
        } catch (e) {
            // Fallback silencieux si audio context échoue
        }
    }, []);

    // Écouter les nouveaux messages
    useEffect(() => {
        const handleNewMessage = (e: Event) => {
            const customEvent = e as CustomEvent;
            const { sender, text } = customEvent.detail;

            // Incrémenter le compteur de messages non lus si le messenger est fermé
            if (!isMessengerOpen) {
                setUnreadCount((prev: number) => prev + 1);
                
                const notification: MessageNotification = {
                    id: `notif_${Date.now()}_${Math.random()}`,
                    sender,
                    preview: text.length > 50 ? text.substring(0, 47) + '...' : text,
                    timestamp: Date.now(),
                    isRead: false
                };

                setNotifications((prev: MessageNotification[]) => [...prev, notification]);
                playNotificationSound();

                // Note: Les notifications ne sont plus supprimées automatiquement
                // Elles restent disponibles dans l'historique via le bouton cloche
            }
        };

        window.addEventListener('CHAT_MESSAGE', handleNewMessage);
        return () => window.removeEventListener('CHAT_MESSAGE', handleNewMessage);
    }, [isMessengerOpen, playNotificationSound]);

    // Écouter l'état du messenger
    useEffect(() => {
        const handleMessengerState = (e: Event) => {
            const customEvent = e as CustomEvent;
            setIsMessengerOpen(customEvent.detail.isOpen);
            
            // Réinitialiser le compteur quand le messenger s'ouvre
            if (customEvent.detail.isOpen) {
                setUnreadCount(0);
                setNotifications((prev: MessageNotification[]) => prev.map(n => ({ ...n, isRead: true })));
            }
        };

        window.addEventListener('MESSENGER_STATE_CHANGE', handleMessengerState);
        return () => window.removeEventListener('MESSENGER_STATE_CHANGE', handleMessengerState);
    }, []);

    // Sauvegarder le compteur de messages non lus
    useEffect(() => {
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        let data = saved ? JSON.parse(saved) : {};
        data.unreadMessageCount = unreadCount;
        localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));
    }, [unreadCount]);

    const dismissNotification = useCallback((notifId: string) => {
        setNotifications((prev: MessageNotification[]) => prev.filter(n => n.id !== notifId));
    }, []);

    const getUnreadCount = useCallback(() => {
        return unreadCount;
    }, [unreadCount]);

    return {
        notifications,
        dismissNotification,
        getUnreadCount,
        setMessengerOpen: setIsMessengerOpen
    };
};